import React, { ComponentType, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { t as outsideComponentT } from 'i18next';
import i18n from 'i18n/i18n';
import { selectDashboardInfo, setDashboardInfo } from 'reducers/dashboardInfo';
import { useDispatch, useSelector } from 'react-redux';

import { SettingsApi } from 'components/pages/settings/settingsApi';
import { isProduction, isStaging, isUAT } from 'config';

import englishFlag from 'assets/lang_flags/en-GB.png';
import swedishFlag from 'assets/lang_flags/sv-SE.png';
import finnishFlag from 'assets/lang_flags/fi-FI.png';
import { useTranslation } from 'react-i18next';

type OnSuccess = (successMsg: string) => void;
type OnError = (error: string) => void;

export type WithLanguageApiProps = { onSuccess?: OnSuccess, onError?: OnError, loader?: JSX.Element; }


type LanguageListReponseDetails = {
    name: string;
    cultureInfo: string;
    imagePath: string;
    imageExtension: string;
    imageUrl: any;
}[];

export type supportedLanguagesData = {
    value: string;
    label: string;
    image: any;
};

export type allSupportedLanguagesData = supportedLanguagesData[];



const emptyInitalLanguageValue = {
    value: '',
    label: '',
    image: null,
};

const parseLanguageList = (
    data: LanguageListReponseDetails
): allSupportedLanguagesData => {
    return data.map((item) => {
        return {
            value: item.cultureInfo,
            label: item.name,
            image: item.imageUrl,
        };
    });
};

const ONE_YEAR_IN_HOURS = 24 * 365;

const withLanguageApi = <P extends WithLanguageApiProps>(WrappedComponent: ComponentType<P>) => {
    const WithLanguageApi: React.FC<P> = (props) => {
        const { onSuccess, onError, loader } = props;

        let cookieDomain = '';

        if (isProduction || isStaging) {
            cookieDomain = '.clevry.com';
        } else if (isUAT) {
            cookieDomain = '.avamae.co.uk';
        }

        const dispatch = useDispatch();
        const { t } = useTranslation('Settings', { i18n });
        const dashboardInfo = useSelector(selectDashboardInfo);

        const [cookies, setCookie] = useCookies(['cultureInfo']);
        const [isLoading, setIsLoading] = useState<boolean>(false);
        const [langDropdownToggle, setLangDropdownToggle] = useState<boolean>(false);
        const [languagesList, setLanguagesList] =
            useState<allSupportedLanguagesData | null>(null);
        const [languageValue, setLanguageValue] = useState<any>(
            emptyInitalLanguageValue
        );

        const backupLanguageList: supportedLanguagesData[] = [
            {
                value: 'en-GB',
                label: t('SSCandidate_Settings_English'),
                image: englishFlag,
            },
            {
                value: 'sv-SE',
                label: t('SSCandidate_Settings_Swedish'),
                image: swedishFlag,
            },
            {
                value: 'fi-FI',
                label: t('SSCandidate_Settings_Finnish'),
                image: finnishFlag,
            },
        ];

        useEffect(() => {
            if (cookies['cultureInfo']) {
                const cultureInfoCookie = cookies['cultureInfo'];
                
                const [languageLabel, languageImage] =
                    handleLabelAndImage(cultureInfoCookie, backupLanguageList);
                
                if (languageLabel !== '') {
                    setLanguageValue({
                        value: cultureInfoCookie,
                        label: languageLabel,
                        image: languageImage,
                    });
                } else {
                    // NOTE: I don't think we want "English" translated here
                    setLanguageValue({
                        value: 'en-GB',
                        label: 'English',
                        image: englishFlag,
                    });
                }
            }
        }, [cookies])
        
        useEffect(() => {
            //TODO - replace with react-query
            (async () => {
                setIsLoading(true);
                let availableLanguages;
                // LanguagesList:
                try {
                    const data = await SettingsApi.getLanguageList();
                    
                    if (data.response && data.response.length > 0) {
                        const parsedAPIData = parseLanguageList(data.response);
                        availableLanguages = parsedAPIData
                        
                        // return parsedAPIData;
                        setLanguagesList(parsedAPIData);
                    } else {
                        console.log('Failed To Get Language List');

                        setLanguagesList([
                            { value: 'en-GB', label: 'English', image: englishFlag },
                        ]);
                    }
                } catch (error) {
                    console.error(error);

                    setLanguagesList([
                        { value: 'en-GB', label: 'English', image: englishFlag },
                    ]);
                }

                // LanguageValue:
                try {
                    if (cookies['cultureInfo']) {
                        const cultureInfoCookie = cookies['cultureInfo'];
                        
                        const [languageLabel, languageImage] =
                            handleLabelAndImage(cultureInfoCookie, availableLanguages);

                        if (languageLabel !== '') {
                            setLanguageValue({
                                value: cultureInfoCookie,
                                label: languageLabel,
                                image: languageImage,
                            });
                        } else {
                            // NOTE: I don't think we want "English" translated here
                            setLanguageValue({
                                value: 'en-GB',
                                label: 'English',
                                image: englishFlag,
                            });
                        }

                        // i18n.changeLanguage(cultureInfoCookie);
                    } else {
                        // NOTE: I don't think we want "English" translated here
                        setLanguageValue({
                            value: 'en-GB',
                            label: 'English',
                            image: englishFlag,
                        });

                        // i18n.changeLanguage('en-GB');
                    }
                } catch (e) {
                    console.log(e);
                    
                    // NOTE: I don't think we want "English" translated here
                    setLanguageValue({
                        value: 'en-GB',
                        label: 'English',
                        image: englishFlag,
                    });

                    // i18n.changeLanguage('en-GB');
                }
                setIsLoading(false);
            })();
        }, []);

        const handleLabelAndImage = (cultureCode: any, avalableLanguages?: allSupportedLanguagesData) => {
            const findItem = avalableLanguages
                ? avalableLanguages.find((item) => item.value === cultureCode)
                : backupLanguageList.find((item) => item.value === cultureCode);

            if (findItem) {
                return [findItem.label, findItem.image];
            }
            return ['', null];
        };

        const toggleLanguageDropdown = () => {
            setLangDropdownToggle(!langDropdownToggle);
        };

        useEffect(() => {
            // Fetch or set the initial language here
            // For example, you could fetch the language from an API or local storage
            const fetchLanguage = async () => {
                // Example: const response = await fetch('/api/language');
                // const data = await response.json();
                // setLanguage(data.language);
            };

            fetchLanguage();
        }, []);

        const handleLanguageDropdown = (e: any) => {
            setLanguageValue(e);
            (async () => {
                const result = await SettingsApi.setLanguage(e.value);
                try{
                    if (result && result?.data === '1') {
                        // Expire in 1 year:
                        const cookieExpireTime = new Date();
                        cookieExpireTime.setHours(
                            cookieExpireTime.getHours() + ONE_YEAR_IN_HOURS
                        );

                        // staging:

                        if (cookieDomain !== '') {
                            setCookie('cultureInfo', e.value, {
                                domain: cookieDomain,
                                expires: cookieExpireTime,
                            });
                        }

                        // localhost:
                        // setCookie('cultureInfo', e.value, { expires: cookieExpireTime });

                        i18n.changeLanguage(e.value);
                        console.log(t('SSCandidate_Settings_Language_Changed'));
                        onSuccess && onSuccess(t('SSCandidate_Settings_Language_Changed'));

                        dispatch(setDashboardInfo({ ...dashboardInfo, cultureCode: e.value }));
                        
                    }
                    if (result.error && result.error.length > 0) {
                        onError && onError(result.error[0]);
                    }
                } catch (e) {
                    console.error(e);
                }
            })();

            toggleLanguageDropdown();
        };

        return isLoading && loader ? loader : <WrappedComponent
            isOpen={langDropdownToggle}
            options={languagesList}
            value={languageValue}
            onChange={handleLanguageDropdown}
            toggleDropdown={() => {
                toggleLanguageDropdown();
            }}
            {...(props as P)}
        />

    };

    return WithLanguageApi;
};

export default withLanguageApi;